import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, ShieldCheck } from "lucide-react";

const PRIMARY = [
  { to: "/equilibrium", label: "Platform", end: true },
  { to: "/equilibrium/modules/spyglass", label: "SpyGlass" },
  { to: "/equilibrium/modules/atlasbridge", label: "AtlasBridge" },
  { to: "/equilibrium/modules/horizon", label: "Horizon" },
  { to: "/equilibrium/modules/dutydeck", label: "DutyDeck" },
  { to: "/equilibrium/modules/oli", label: "OLI" },
];

export default function EquilibriumLayout({ children }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    document.title = "Equilibrium-AI — Operational Intelligence";
    return () => {
      document.title = "Axiom-AI — Operator-Grade Supply Chain Intelligence";
    };
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-axiom-bg text-axiom-text" data-testid="equilibrium-layout">
      <header
        data-testid="eq-nav"
        className={`fixed top-0 inset-x-0 z-40 transition-all duration-300 ${
          scrolled ? "bg-axiom-bg/85 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
        }`}
      >
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="h-16 md:h-20 flex items-center justify-between gap-6">
            <Link
              to="/equilibrium"
              data-testid="eq-brand"
              className="flex items-center gap-3"
            >
              <div className="w-9 h-9 border border-axiom-accent/40 flex items-center justify-center relative">
                <span className="signal-dot animate-pulse-signal" style={{ width: 6, height: 6 }} />
                <span className="absolute inset-0 m-1 border border-axiom-accent/20" />
              </div>
              <div className="leading-tight">
                <div className="font-display text-lg md:text-xl font-bold tracking-tight">
                  EQUILIBRIUM-AI
                </div>
                <div className="num-label">OPERATIONAL · INTELLIGENCE</div>
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-6">
              {PRIMARY.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  data-testid={`eq-nav-${n.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `text-sm font-medium transition-colors ${
                      isActive
                        ? "text-axiom-text"
                        : "text-axiom-muted hover:text-axiom-text"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-2">
              <button
                data-testid="eq-cta-console"
                onClick={() => navigate("/equilibrium/console")}
                className="btn-axiom text-xs"
              >
                <ShieldCheck size={14} /> Operator Console
              </button>
              <button
                data-testid="eq-mobile-toggle"
                className="lg:hidden text-axiom-text p-2"
                onClick={() => setOpen((v) => !v)}
                aria-label="toggle menu"
              >
                {open ? <X size={20} /> : <Menu size={20} />}
              </button>
            </div>
          </div>
        </div>

        {open && (
          <div
            data-testid="eq-mobile-menu"
            className="lg:hidden bg-axiom-bg/95 backdrop-blur-xl border-t border-white/5"
          >
            <div className="max-w-[1400px] mx-auto px-4 py-3 flex flex-col">
              {PRIMARY.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) =>
                    `py-3 px-2 border-b border-white/5 text-sm ${
                      isActive ? "text-axiom-text" : "text-axiom-muted"
                    }`
                  }
                >
                  {n.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>

      <main className="pt-20">{children}</main>

      <EquilibriumFooter />
    </div>
  );
}

function EquilibriumFooter() {
  return (
    <footer
      data-testid="eq-footer"
      className="mt-24 border-t border-white/5 bg-axiom-surface/40"
    >
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="font-display text-2xl sm:text-3xl text-axiom-text font-bold leading-tight max-w-md">
            Equilibrium-AI keeps operational truth coherent long enough for intervention.
          </div>
          <p className="mt-4 text-sm text-axiom-dim max-w-md">
            A modular intelligence ecosystem for supply-chain entropy reduction, signal coherence,
            and provenance-grade accountability.
          </p>
        </div>
        <div className="lg:col-span-3">
          <div className="num-label mb-3">MODULES</div>
          <ul className="space-y-2 text-sm">
            {PRIMARY.slice(1).map((p) => (
              <li key={p.to}>
                <Link
                  to={p.to}
                  className="text-axiom-muted hover:text-axiom-text transition-colors"
                >
                  {p.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <div className="lg:col-span-4">
          <div className="num-label mb-3">STATUS</div>
          <ul className="space-y-2 text-xs font-mono text-axiom-muted">
            <li className="flex items-center gap-2">
              <span className="signal-dot animate-pulse-signal" style={{ width: 6, height: 6 }} />
              SYSTEM · COHERENT
            </li>
            <li className="flex items-center gap-2">
              <span className="signal-dot opacity-50" style={{ width: 6, height: 6 }} />
              PROVENANCE · APPEND-ONLY · ACTIVE
            </li>
            <li className="flex items-center gap-2">
              <span className="signal-dot opacity-50" style={{ width: 6, height: 6 }} />
              ACCOUNTABILITY · LEDGER · v0.12
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-5 font-mono text-[11px] text-axiom-dim flex flex-col md:flex-row md:items-center justify-between gap-2">
          <span>
            © {new Date().getFullYear()} EQUILIBRIUM-AI / OPERATIONAL INTELLIGENCE / BY AXIOM-AI
          </span>
          <span>equilibrium-ai-edi.solutions</span>
        </div>
      </div>
    </footer>
  );
}
